// src/components/EditableVATInvoice.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Image,
  BlobProvider,
} from "@react-pdf/renderer";

// PDF Styles (same as ProfessionalVATInvoice)
const pdfStyles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#0891b2",
    paddingBottom: 20,
  },
  companyName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0891b2",
  },
  companyDetails: {
    fontSize: 10,
    color: "#666",
    marginTop: 5,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0891b2",
    textAlign: "right",
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
  },
  subtitle0: {
    fontSize: 10,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
    fontWeight: "bold",
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    backgroundColor: "#f8f9fa",
    padding: 5,
    borderRadius: 5,
  },
  infoBlock: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 10,
    color: "#666",
    marginBottom: 3,
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333",
  },
  customerSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#ddd",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0891b2",
    color: "white",
    padding: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    padding: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9f9f9",
  },
  col1: { width: "35%", paddingHorizontal: 5 },
  col2: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col3: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col4: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col5: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col6: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  totals: {
    marginTop: 20,
    alignItems: "flex-end",
  },
  totalRow: {
    flexDirection: "row",
    padding: 5,
    width: "50%",
  },
  totalLabel: {
    flex: 1,
    textAlign: "right",
    marginRight: 10,
    fontSize: 11,
  },
  totalValue: {
    width: 120,
    textAlign: "right",
    fontSize: 11,
  },
  grandTotal: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#0891b2",
    borderTopWidth: 2,
    borderTopColor: "#0891b2",
    paddingTop: 10,
    marginTop: 5,
  },
  notes: {
    marginTop: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  notesTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#0891b2",
  },
  notesText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: "#666",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#666",
    fontSize: 9,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 10,
  },
  compliance: {
    fontSize: 8,
    color: "#0891b2",
    marginTop: 5,
  },
  logo: {
    width: 30,
    height: 30,
  },
});

// PDF Document Component
const InvoicePDFDocument = ({ invoiceData }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: "AED",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View>
            <Image src={invoiceData.company.logo} style={{ width: 90, height: 110, marginBottom: 5, marginLeft: 100 }} />
          </View>
          <View>
            <Text style={pdfStyles.invoiceTitle}>TAX INVOICE</Text>
            <Text style={pdfStyles.subtitle0}>Diamond Quality Construction L.L.C.SP</Text>
            <Text style={pdfStyles.subtitle}>TRN: 104836477000001</Text>
            <Text style={pdfStyles.subtitle}>Sharjah - Muweilah - University City Street</Text>
            <Text style={pdfStyles.subtitle}>Office No.: 602-58 Floor No.: 6 - Al Areej</Text>
            <Text style={pdfStyles.subtitle}>0557001522</Text>
            <Text style={pdfStyles.subtitle}>dqcc01@gmail.com</Text>
            <Text style={pdfStyles.subtitle}>www.dqcc.ae</Text>
          </View>
        </View>

        {/* Invoice Info */}
        <View style={pdfStyles.infoSection}>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Number</Text>
            <Text style={pdfStyles.infoValue}>{invoiceData.invoiceNo}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Date</Text>
            <Text style={pdfStyles.infoValue}>
              {formatDate(invoiceData.invoiceDate)}
            </Text>
          </View>
          {/* <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Due Date</Text>
            <Text style={pdfStyles.infoValue}>
              {formatDate(invoiceData.dueDate)}
            </Text>
          </View> */}
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Payment Status</Text>
            <Text style={pdfStyles.infoValue}>{invoiceData.status}</Text>
          </View>
        </View>

        {/* Customer Details */}
        <View style={pdfStyles.customerSection}>
          <Text style={pdfStyles.infoLabel}>Bill To:</Text>
          <Text
            style={[pdfStyles.infoValue, { marginTop: 5, marginBottom: 3 }]}
          >
            {invoiceData.customerName}
          </Text>
          <Text style={pdfStyles.companyDetails}>
            {invoiceData.customerAddress}
          </Text>
          {invoiceData.customerTrn && (
            <Text style={pdfStyles.companyDetails}>
              TRN: {invoiceData.customerTrn}
            </Text>
          )}
          <Text style={pdfStyles.companyDetails}>
            {invoiceData.customerCity}, {invoiceData.customerCountry}
          </Text>
          {invoiceData.customerPhone && (
            <Text style={pdfStyles.companyDetails}>
              Phone: {invoiceData.customerPhone}
            </Text>
          )}
        </View>

        {/* Items Table */}
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.col1}>Description</Text>
            <Text style={pdfStyles.col2}>Qty</Text>
            <Text style={pdfStyles.col3}>Unit Price</Text>
            <Text style={pdfStyles.col4}>Discount</Text>
            <Text style={pdfStyles.col5}>VAT</Text>
            <Text style={pdfStyles.col6}>Total</Text>
          </View>

          {invoiceData.items.map((item, index) => {
            const subtotal = item.quantity * item.unit_price;
            const discountAmount = item.discount_amount;
            const afterDiscount = subtotal - discountAmount;
            const vatAmount = afterDiscount * (item.vat_percent / 100);
            const total = afterDiscount + vatAmount;

            return (
              <View
                key={index}
                style={[
                  pdfStyles.tableRow,
                  index % 2 === 1 ? pdfStyles.tableRowAlt : {},
                ]}
              >
                <Text style={pdfStyles.col1}>{item.description}</Text>
                <Text style={pdfStyles.col2}>
                  {item.quantity} {item.unit}
                </Text>
                <Text style={pdfStyles.col3}>
                  {formatCurrency(item.unit_price)}
                </Text>
                <Text style={pdfStyles.col4}>
                  {formatCurrency(item.discount_amount)}
                </Text>
                <Text style={pdfStyles.col5}>{item.vat_percent}%</Text>
                <Text style={pdfStyles.col6}>{formatCurrency(total)}</Text>
              </View>
            );
          })}
        </View>

        {/* Totals Section */}
        <View style={pdfStyles.totals}>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Subtotal:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.subtotal)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Total Discount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.totalDiscount)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Taxable Amount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.taxableAmount)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>VAT Amount (5%):</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.vatTotal)}
            </Text>
          </View>
          <View style={[pdfStyles.totalRow, pdfStyles.grandTotal]}>
            <Text style={pdfStyles.totalLabel}>Total Amount Due:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals.totalDue)}
            </Text>
          </View>
        </View>

        {/* Notes */}
        {invoiceData.notes && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesTitle}>Notes / Terms:</Text>
            <Text style={pdfStyles.notesText}>{invoiceData.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>
            This is a computer generated invoice
          </Text>
          {/* <Text style={pdfStyles.compliance}>
            🇦🇪 UAE VAT Compliant | Federal Tax Authority Approved | TRN:{" "}
            {invoiceData.company.trn}
          </Text> */}
        </View>
      </Page>
    </Document>
  );
};

const EditableVATInvoice = ({
  onSubmit = () => {},
  initialData,
  companyInfo = null,
}) => {
  // Helper function to format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const [formData, setFormData] = useState(
    initialData || {
      id: null,
      invoiceNo: "",
      invoiceDate: "",
      dueDate: "",
      mode: null,
      status: "Unpaid",
      customerId: null,

      // Customer Information
      customerName: "",
      customerTrn: "",
      customerPhone: "",
      customerAddress: "",
      customerCity: "",
      customerCountry: "",

      // Notes
      notes: "",
    }
  );

  const [company] = useState(
    companyInfo || {
      name: "Diamond Quality Constructions",
      trn: "104836477000001",
      address: "Sharja, UAE",
      logo: "/logo.jpeg",
    }
  );

  const [items, setItems] = useState(initialData?.items || []);
  const [errors, setErrors] = useState({});
  const [pdfData, setPdfData] = useState(null);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [receiptExists, setReceiptExists] = useState(false);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Check if receipt exists for this invoice using initialData
  useEffect(() => {
    let hasReceipt = false;
    
    if (initialData) {
      console.log('🔍 Checking receipts - initialData:', initialData);
      console.log('📋 receipts field:', initialData.receipts);
      
      if (initialData.receipts) {
        // Laravel API returns receipts as an array when serialized
        const receiptsArray = Array.isArray(initialData.receipts) 
          ? initialData.receipts 
          : (initialData.receipts.all || []);
        
        hasReceipt = receiptsArray.length > 0;
        console.log('✓ Receipt found:', hasReceipt, 'Count:', receiptsArray.length);
      } else {
        console.log('✗ No receipts field in initialData');
      }
    }
    
    setReceiptExists(hasReceipt);
  }, [initialData]);

  // UAE Emirates list
  const emirates = [
    "Abu Dhabi",
    "Dubai",
    "Sharjah",
    "Ajman",
    "Umm Al Quwain",
    "Ras Al Khaimah",
    "Fujairah",
  ];

  // Calculate line item total
  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const discountAmount = item.discount_amount;
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = afterDiscount * (item.vat_percent / 100);
    return afterDiscount + vatAmount;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let vatTotal = 0;

    items.forEach((item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = item.discount_amount;
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemVat = itemAfterDiscount * (item.vat_percent / 100);

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
      taxableAmount += itemAfterDiscount;
      vatTotal += itemVat;
    });

    const totalDue = taxableAmount + vatTotal;

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      vatTotal,
      totalDue,
    };
  };

  const { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue } =
    calculateTotals();

  // Add new item row
  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        quantity: 1,
        unit: "Unit",
        unit_price: 0,
        discount_amount: 0,
        vat_percent: 5,
      },
    ]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  // Update item field
  const updateItem = (index, field, value) => {
    const newItems = [...items];

    if (
      ["quantity", "unit_price", "discount_amount", "vat_percent"].includes(
        field
      )
    ) {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }

    setItems(newItems);
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "/");
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName)
      newErrors.customerName = "Customer name is required";
    if (!formData.invoiceDate) newErrors.invoiceDate = "Invoice date is required";

    // Prevent status change if receipt exists
    if (receiptExists) {
      newErrors.receiptLocked = "Cannot modify invoice when receipt exists";
    }

    // Validate payment mode is required when status is 'Paid' (only if no receipt)
    if (formData.status === "Paid" && !formData.mode && !receiptExists) {
      newErrors.mode = "Payment mode is required when status is Paid";
    }

    items.forEach((item, index) => {
      if (!item.description) {
        newErrors[`item_${index}_description`] = "Description required";
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = "Quantity must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit - show modal first
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowSaveModal(true);
    }
  };

  // Handle actual invoice update
  const saveInvoice = async (downloadPDF = false) => {
    setShowSaveModal(false);
    setIsSubmitting(true);

    try {
      // Prepare API payload for update
      const updatePayload = {
        customer_id: formData.customerId,
        invoice_date: formData.invoiceDate,
        due_date: formData.dueDate,
        status: formData.status,
        mode: formData.status === "Unpaid" ? "" : formData.mode || "",
        notes: formData.notes,
        items: items.map((item) => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          vat_percent: parseFloat(item.vat_percent) || 0,
        })),
      };

      // Send update request to API
      const response = await axios.put(
        `${apiUrl}/api/invoices/${formData.id}`,
        updatePayload
      );

      // Prepare display data for PDF
      const invoiceData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
        formattedDates: {
          invoiceDate: formatDateForDisplay(formData.invoiceDate),
          dueDate: formatDateForDisplay(formData.dueDate),
        },
      };

      // Prepare PDF data
      const pdfInvoiceData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
      };

      // Set PDF data and trigger download only if user selected the option
      if (downloadPDF) {
        setPdfData(pdfInvoiceData);
        setShouldDownload(true);
      }

      // Call onSubmit callback if provided
      if (onSubmit && onSubmit !== (() => {})) {
        onSubmit(invoiceData);
      } else {
        // Default behavior: show success message
        if (downloadPDF) {
          alert("Invoice updated successfully! PDF will be downloaded automatically.");
        } else {
          setSuccessMessage(formData.invoiceNo);
        }
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
      alert(
        error.response?.data?.message ||
          "Error updating invoice. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Effect to handle PDF download
  useEffect(() => {
    if (shouldDownload && pdfData) {
      const generateAndDownloadPDF = async () => {
        try {
          const blob = await pdf(
            <InvoicePDFDocument invoiceData={pdfData} />
          ).toBlob();

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const fileName = `Invoice-${pdfData.invoiceNo || "Updated"}.pdf`;
          link.download = fileName;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          // Reset the trigger
          setShouldDownload(false);
          setPdfData(null);
        } catch (error) {
          console.error("Error generating PDF:", error);
          alert("Error generating PDF. Please try again.");
          setShouldDownload(false);
          setPdfData(null);
        }
      };

      generateAndDownloadPDF();
    }
  }, [shouldDownload, pdfData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#23A19B] rounded-t-2xl p-8 text-white shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Edit VAT Invoice</h1>
              <p className="text-white/80">
                Invoice #{formData.invoiceNo} • UAE Tax Compliant
              </p>
            </div>
            <div className="badge badge-lg bg-white/20 text-white border-0 p-4">
              <span className="text-2xl font-bold">✏️</span>
            </div>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-8 mb-8 border-t-4 border-primary">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Invoice Details Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Invoice Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-primary">📄</span> INVOICE NO.
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 focus:border-primary bg-base-200 cursor-not-allowed"
                    value={formData.invoiceNo}
                    readOnly
                    placeholder="INV-00001"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cannot be changed
                  </p>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-secondary">📅</span> INVOICE DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className={`input input-bordered border-2 focus:border-secondary ${
                      errors.invoiceDate ? "input-error" : ""
                    }`}
                    value={formData.invoiceDate}
                    onChange={(e) =>
                      updateField("invoiceDate", e.target.value)
                    }
                  />
                  {errors.invoiceDate && (
                    <span className="text-error text-sm mt-1">
                      {errors.invoiceDate}
                    </span>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-info">💰</span> PAYMENT STATUS
                      {receiptExists && <span className="badge badge-warning badge-sm ml-2">Locked</span>}
                    </span>
                  </label>
                  <select
                    className={`select select-bordered border-2 ${
                      receiptExists 
                        ? "bg-base-200 cursor-not-allowed opacity-50 border-warning" 
                        : "focus:border-info"
                    }`}
                    value={formData.status}
                    onChange={(e) => !receiptExists && updateField("status", e.target.value)}
                    disabled={receiptExists}
                  >
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                  </select>
                  {receiptExists && (
                    <p className="text-xs text-warning mt-1 font-semibold">
                      🔒 Receipt exists - Status is locked and cannot be changed
                    </p>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      MODE OF PAYMENT{" "}
                      {formData.status === "Paid" && (
                        <span className="text-error">*</span>
                      )}
                      {receiptExists && <span className="badge badge-warning badge-sm">Locked</span>}
                    </span>
                  </label>
                  <select
                    className={`select select-bordered border-2 ${
                      formData.status === "Paid" && !receiptExists
                        ? `focus:border-info ${
                            errors.mode ? "select-error" : ""
                          }`
                        : "opacity-50 cursor-not-allowed bg-base-200"
                    } ${errors.mode ? "select-error" : ""}`}
                    value={formData.mode || ""}
                    onChange={(e) =>
                      updateField("mode", e.target.value || null)
                    }
                    disabled={formData.status !== "Paid" || receiptExists}
                  >
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                  {errors.mode && !receiptExists && (
                    <span className="text-error text-sm mt-1">
                      {errors.mode}
                    </span>
                  )}
                  {receiptExists && (
                    <p className="text-xs text-warning mt-1">
                      ⚠️ Receipt exists for this invoice. Payment details are locked.
                    </p>
                  )}
                </div>

                <div className="form-control hidden">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-accent">⏰</span> DUE DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered border-2 focus:border-accent"
                    value={formData.dueDate}
                    onChange={(e) => updateField("dueDate", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Customer Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-secondary flex items-center gap-2">
                <span className="w-1 h-6 bg-secondary rounded-full"></span>
                Bill To — Customer
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      CUSTOMER NAME *
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerName}
                    readOnly
                    placeholder="Customer name"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cannot be changed</p>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      TRN (TAX REG. NO.)
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 font-mono bg-base-200 cursor-not-allowed"
                    value={formData.customerTrn}
                    readOnly
                    placeholder="15-digit TRN"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">PHONE</span>
                  </label>
                  <input
                    type="tel"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerPhone}
                    readOnly
                    placeholder="+971 50 123 4567"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">ADDRESS</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerAddress}
                    readOnly
                    placeholder="Street, Area"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      CITY / EMIRATE
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2 bg-base-200 cursor-not-allowed opacity-75"
                    value={formData.customerCity}
                    disabled
                  >
                    <option value="">{formData.customerCity || "Select..."}</option>
                    {emirates.map((emirate) => (
                      <option key={emirate} value={emirate}>
                        {emirate}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">COUNTRY</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.customerCountry}
                    readOnly
                    placeholder="UAE"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-accent mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-accent rounded-full"></span>
                Line Items
              </h3>

              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                    <tr>
                      <th className="rounded-tl-lg">#</th>
                      <th>DESCRIPTION</th>
                      <th>QTY</th>
                      <th>UNIT</th>
                      <th>UNIT PRICE</th>
                      <th>DISC</th>
                      <th>VAT %</th>
                      <th className="rounded-tr-lg">TOTAL</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const itemTotal = calculateItemTotal(item);

                      return (
                        <tr
                          key={index}
                          className="hover:bg-base-200 transition-colors"
                        >
                          <td className="font-bold text-primary">
                            {index + 1}
                          </td>
                          <td>
                            <input
                              type="text"
                              className={`input input-bordered input-sm w-40 ${
                                errors[`item_${index}_description`]
                                  ? "input-error"
                                  : ""
                              }`}
                              value={item.description}
                              onChange={(e) =>
                                updateItem(index, "description", e.target.value)
                              }
                              placeholder="Description"
                            />
                            {errors[`item_${index}_description`] && (
                              <p className="text-error text-xs mt-1">
                                {errors[`item_${index}_description`]}
                              </p>
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              className={`input input-bordered input-sm w-16 ${
                                errors[`item_${index}_quantity`]
                                  ? "input-error"
                                  : ""
                              }`}
                              value={item.quantity}
                              onChange={(e) =>
                                updateItem(index, "quantity", e.target.value)
                              }
                              min="1"
                              step="0.01"
                            />
                            {errors[`item_${index}_quantity`] && (
                              <p className="text-error text-xs mt-1">
                                {errors[`item_${index}_quantity`]}
                              </p>
                            )}
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-20"
                              value={item.unit}
                              onChange={(e) =>
                                updateItem(index, "unit", e.target.value)
                              }
                            >
                              <option>Unit</option>
                              <option>Hour</option>
                              <option>Day</option>
                              <option>Service</option>
                              <option>Piece</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-20"
                              value={item.unit_price}
                              onChange={(e) =>
                                updateItem(index, "unit_price", e.target.value)
                              }
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="input input-bordered input-sm w-16"
                              value={item.discount_amount}
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  "discount_amount",
                                  e.target.value
                                )
                              }
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td>
                            <select
                              className="select select-bordered select-sm w-16"
                              value={item.vat_percent}
                              onChange={(e) =>
                                updateItem(index, "vat_percent", e.target.value)
                              }
                            >
                              <option value="0">0%</option>
                              <option value="5">5%</option>
                            </select>
                          </td>
                          <td className="text-right font-mono font-bold text-success">
                            AED {itemTotal.toFixed(2)}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs text-error hover:bg-error/20"
                              onClick={() => removeItem(index)}
                              disabled={items.length === 1}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className="btn btn-outline btn-accent btn-sm mt-4 hover:scale-105 transition-transform"
                onClick={addItem}
              >
                ➕ Add Line Item
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="stat bg-gradient-to-br from-info/20 to-info/5 rounded-xl">
                <div className="stat-figure text-info">
                  <span className="text-2xl">💰</span>
                </div>
                <div className="stat-title">Subtotal</div>
                <div className="stat-value text-info text-2xl">
                  AED {subtotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-warning/20 to-warning/5 rounded-xl">
                <div className="stat-figure text-warning">
                  <span className="text-2xl">🏷️</span>
                </div>
                <div className="stat-title">Discount</div>
                <div className="stat-value text-warning text-2xl">
                  AED {totalDiscount.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-secondary/20 to-secondary/5 rounded-xl">
                <div className="stat-figure text-secondary">
                  <span className="text-2xl">🧾</span>
                </div>
                <div className="stat-title">VAT (5%)</div>
                <div className="stat-value text-secondary text-2xl">
                  AED {vatTotal.toFixed(2)}
                </div>
              </div>

              <div className="stat bg-gradient-to-br from-success/20 to-success/5 rounded-xl">
                <div className="stat-figure text-success">
                  <span className="text-2xl">💵</span>
                </div>
                <div className="stat-title">Total Due</div>
                <div className="stat-value text-success text-2xl">
                  AED {totalDue.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-gradient-to-br from-info/10 to-info/5 p-6 rounded-xl">
                <label className="label">
                  <span className="label-text font-bold text-info text-lg flex items-center gap-2">
                    <span className="w-1 h-6 bg-info rounded-full"></span>
                    NOTES / TERMS
                  </span>
                </label>
                <textarea
                  className="textarea textarea-bordered h-32 w-full border-2 focus:border-info"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="Add any notes or terms..."
                ></textarea>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <button
                type="button"
                className="btn btn-ghost btn-lg hover:bg-base-200"
                onClick={() => window.history.back()}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 hover:scale-105 transition-transform shadow-lg disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    ✏️ Update Invoice
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-base-500 mt-4">
          <p>
            🇦🇪 UAE VAT Compliant | Status: {formData.status} | Mode:{" "}
            {formData.mode || "N/A"}
          </p>
        </div>

        {/* Save Options Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-primary mb-2">
                  Update Invoice
                </h2>
                <p className="text-gray-600">What would you like to do?</p>
              </div>

              <div className="space-y-4">
                {/* Option 1: Update Only */}
                <button
                  onClick={() => saveInvoice(false)}
                  disabled={isSubmitting}
                  className="w-full btn btn-outline btn-lg border-2 border-primary text-primary hover:bg-primary hover:border-primary disabled:opacity-50"
                >
                  <span className="text-xl mr-2">✏️</span>
                  Update Invoice Only
                </button>

                {/* Option 2: Update & Print PDF */}
                <button
                  onClick={() => saveInvoice(true)}
                  disabled={isSubmitting}
                  className="w-full btn btn-primary btn-lg bg-gradient-to-r from-primary to-secondary border-0 disabled:opacity-50"
                >
                  <span className="text-xl mr-2">📄</span>
                  Update & Print PDF
                </button>
              </div>

              <div className="mt-6 pt-6 border-t text-center">
                <button
                  onClick={() => setShowSaveModal(false)}
                  disabled={isSubmitting}
                  className="btn btn-ghost btn-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Message Modal */}
        {successMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="mb-6 text-center">
                <div className="text-6xl text-success mb-4">✓</div>
                <h2 className="text-2xl font-bold text-success mb-2">
                  Invoice Updated Successfully
                </h2>
                <p className="text-lg text-primary font-semibold">
                  Invoice No:{" "}
                  <span className="text-secondary font-bold">
                    {successMessage}
                  </span>
                </p>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setSuccessMessage(null);
                    window.location.href = "/invoices";
                  }}
                  className="btn btn-primary btn-lg"
                >
                  Back to Invoices
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{``}</style>
    </div>
  );
};

export default EditableVATInvoice;
